// UOM standardization tables — MUST stay in sync with the backend
// services/org_dashboard/usage_normalizer.py conversion tables.
// All multipliers convert FROM the input UOM TO the standard reporting UOM
// for that commodity (Electricity→kWh, Gas→therms, Water→gallons).

export const ELEC_TO_KWH = { kwh: 1, mwh: 1000, gwh: 1_000_000 };

export const GAS_TO_THERMS = {
  therms: 1, therm: 1, thm: 1, th: 1,
  ccf: 1.037, mcf: 10.37, dekatherms: 10, dth: 10, mmbtu: 10, kwh: 0.0341214,
};

export const WATER_TO_GAL = {
  gallons: 1, gallon: 1, gal: 1, gals: 1, galloons: 1,
  kgal: 1000, tgal: 1000, tgals: 1000, kgalus: 1000,
  ccf: 748.052, hcf: 748.052, cf: 7.48052, cuft: 7.48052,
  mcf: 7480.52, dcf: 7.48052, units: 748.052, m3: 264.172,
};

const STD_UOM = { electricity: 'kWh', gas: 'therms', water: 'gallons' };
const TABLES = { electricity: ELEC_TO_KWH, gas: GAS_TO_THERMS, water: WATER_TO_GAL };

export function bucketOf(commodity) {
  const c = (commodity || '').toLowerCase().trim();
  if (c.startsWith('elec')) return 'electricity';
  if (c.startsWith('gas') || c.includes('natural')) return 'gas';
  if (c.startsWith('water')) return 'water';
  return null;
}

// Human-friendly label for a raw (lower-cased) UOM token.
export function prettyUom(u) {
  const key = (u || '').toLowerCase().trim();
  const map = {
    kwh: 'kWh', mwh: 'MWh', gwh: 'GWh',
    therms: 'therms', therm: 'therms', thm: 'therms', th: 'therms',
    ccf: 'CCF', hcf: 'HCF', mcf: 'MCF', cf: 'CF', cuft: 'CF', dcf: 'DCF',
    dth: 'Dth', dekatherms: 'Dth', mmbtu: 'MMBtu',
    gallons: 'gallons', gallon: 'gallons', gal: 'gallons', gals: 'gallons', galloons: 'gallons',
    kgal: 'kGal', tgal: 'kGal', tgals: 'kGal', kgalus: 'kGal', units: 'units', m3: 'm³',
  };
  return map[key] || (u || '');
}

const toNum = (v) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

// Merge an original-UOM breakdown [{uom, qty, bills}] into ordered unique
// DISPLAY labels (case-insensitive; e.g. gallons/gal/gals → "gallons"),
// summing quantities so the largest source unit comes first.
export function dedupeUomLabels(list) {
  const byLabel = new Map();
  for (const r of list || []) {
    const label = prettyUom(r.uom);
    if (!label) continue;
    byLabel.set(label, (byLabel.get(label) || 0) + (Number(r.qty) || 0));
  }
  return [...byLabel.entries()].sort((a, b) => b[1] - a[1]).map(([l]) => l);
}

// Standardize representative usage lines ({commodity, quantity, uom}) into
// conversion rows the UI can display (as-billed → reporting unit).
export function standardizeUsageLines(lines) {
  return (lines || []).map((l) => {
    const bucket = bucketOf(l.commodity);
    if (!bucket) return null;
    const q = toNum(l.quantity);
    if (q <= 0) return null;
    const table = TABLES[bucket];
    const u = (l.uom || '').toLowerCase().trim();
    const factor = table[u];
    if (factor == null) {
      return {
        commodity: l.commodity, bucket, origQty: q,
        origUom: prettyUom(l.uom) || STD_UOM[bucket],
        stdUom: STD_UOM[bucket], stdQty: null, unknown: true,
      };
    }
    return {
      commodity: l.commodity, bucket, origQty: q,
      origUom: prettyUom(l.uom) || STD_UOM[bucket],
      stdUom: STD_UOM[bucket], factor, stdQty: q * factor, converted: factor !== 1,
    };
  }).filter(Boolean);
}
